import React, { useState, useEffect } from 'react';
// Remove Link import if not used elsewhere
// import { Link } from 'react-router-dom';
import { CATEGORIES, fetchDbPosts, addPost, deletePost, uploadCoverImage } from '../data/blogData';
import { ADMIN_PASSWORD } from '../data/supabaseClient';
import Icon from '../components/Icon';
import './Admin.css';

const AUTH_KEY = 'lms_admin_auth';

const EMPTY = {
  title: '', tag: '', category: 'investing', excerpt: '',
  body: '', author: 'G.M. Narendra', read: '5 min read',
  linkedinUrl: '', // Add LinkedIn URL field
};

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [pwd, setPwd] = useState('');
  const [err, setErr] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState('');
  const [posts, setPosts] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(AUTH_KEY) === '1') setAuthed(true);
  }, []);

  useEffect(() => {
    if (authed) loadPosts();
  }, [authed]);

  const loadPosts = async () => {
    try { setPosts(await fetchDbPosts()); }
    catch (e) { setMsg('Could not load posts: ' + e.message); }
  };

  const login = () => {
    if (pwd === ADMIN_PASSWORD) {
      sessionStorage.setItem(AUTH_KEY, '1');
      setAuthed(true); setErr('');
    } else { setErr('Incorrect password.'); }
  };

  const logout = () => {
    sessionStorage.removeItem(AUTH_KEY);
    setAuthed(false); setPwd('');
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgFile(file);
    const reader = new FileReader();
    reader.onload = () => setImgPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setMsg('Title and content are required.');
      return;
    }
    if (!form.linkedinUrl.trim()) {
      setMsg('LinkedIn post URL is required.');
      return;
    }
    setBusy(true);
    setMsg('Publishing…');
    try {
      let imgUrl = '';
      if (imgFile) imgUrl = await uploadCoverImage(imgFile);
      await addPost({
        title: form.title.trim(),
        tag: form.tag.trim() || 'General',
        category: form.category,
        excerpt: form.excerpt.trim() || form.body.slice(0, 160) + '…',
        body: form.body,
        author: form.author.trim() || 'G.M. Narendra',
        read: form.read.trim() || '5 min read',
        img: imgUrl || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80',
        linkedinUrl: form.linkedinUrl.trim(), // Add LinkedIn URL
      });
      setForm(EMPTY); setImgFile(null); setImgPreview('');
      await loadPosts();
      setMsg('✅ Blog published! It is now live for everyone on the Blogs page.');
      setTimeout(() => setMsg(''), 5000);
    } catch (e) {
      setMsg('❌ Failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try { await deletePost(id); await loadPosts(); }
    catch (e) { setMsg('Delete failed: ' + e.message); }
  };

  if (!authed) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <h1>Admin Login</h1>
          <p>Enter the admin password to manage blog posts.</p>
          <input
            type="password" placeholder="Password" value={pwd}
            onChange={e => setPwd(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && login()}
          />
          {err && <div className="admin-err">{err}</div>}
          <button className="btn-primary" onClick={login}>Login</button>
          <a href="/blogs" className="admin-back-link">← Back to Blog</a>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-wrap container">
        <div className="admin-head">
          <h1>Manage Blog Posts</h1>
          <button className="admin-logout" onClick={logout}>Logout</button>
        </div>

        <div className="admin-card">
          <h2>Add New Blog Post</h2>
          {msg && <div className="admin-msg">{msg}</div>}

          <label>Title *</label>
          <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Blog title" />

          <div className="admin-row">
            <div>
              <label>Tag</label>
              <input value={form.tag} onChange={e => set('tag', e.target.value)} placeholder="e.g. Mutual Funds" />
            </div>
            <div>
              <label>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-row">
            <div>
              <label>Author</label>
              <input value={form.author} onChange={e => set('author', e.target.value)} />
            </div>
            <div>
              <label>Read time</label>
              <input value={form.read} onChange={e => set('read', e.target.value)} placeholder="5 min read" />
            </div>
          </div>

          <label>LinkedIn Post URL *</label>
          <input 
            value={form.linkedinUrl} 
            onChange={e => set('linkedinUrl', e.target.value)} 
            placeholder="https://www.linkedin.com/posts/..." 
          />
          <small style={{ color: '#888', fontSize: '0.85rem' }}>
            The full article will be hosted on LinkedIn. This URL will be used for the "Read Full Article" button.
          </small>

          <label>Short excerpt (optional)</label>
          <textarea rows={2} value={form.excerpt} onChange={e => set('excerpt', e.target.value)} placeholder="One-line summary shown on the blog card" />

          <label>Full content * (one paragraph per line)</label>
          <textarea rows={8} value={form.body} onChange={e => set('body', e.target.value)} placeholder="Write the full article here…" />

          <label>Cover image</label>
          <input type="file" accept="image/*" onChange={handleImage} />
          {imgPreview && <img src={imgPreview} alt="preview" className="admin-preview" />}

          <button className="btn-primary admin-submit" onClick={submit} disabled={busy}>
            {busy ? 'Publishing…' : 'Publish Blog'}
          </button>
        </div>

        <div className="admin-card">
          <h2>Published Posts ({posts.length})</h2>
          {posts.length === 0 && <p className="admin-empty">No posts added yet.</p>}
          <div className="admin-list">
            {posts.map(p => (
              <div key={p.id} className="admin-list-item">
                <img src={p.img} alt={p.title} />
                <div className="admin-li-info">
                  <strong>{p.title}</strong>
                  <span>{p.tag} · {p.date}</span>
                  {p.linkedinUrl && (
                    <a 
                      href={p.linkedinUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="admin-li-linkedin"
                    >
                      View on LinkedIn
                    </a>
                  )}
                </div>
                <div className="admin-li-actions">
                  <button onClick={() => remove(p.id)} className="admin-del">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}